import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Drawer, Form, Select, Space } from 'antd';
import debounce from 'lodash.debounce';
import { filterRangePresets } from 'shared/constants/filters';
import type { ITripFilter } from 'shared/stores/use-trip-filters.ts';
import { useTripFiltersStore } from 'shared/stores/use-trip-filters.ts';
import { transformAndSetFilters } from 'shared/utils/filters/transform-and-set-filters.ts';
import { ClientType } from 'types/client';
import { ClientFormSelect } from 'components/common/client-form-select';
import { DataRangeFormPicker } from 'components/common/data-range-form-picker';
import { UserFormSelect } from 'components/common/user-form-select';
import { WarehouseFormSelect } from 'components/common/warehouse-form-select';
import styles from './trips-filters-drawer.module.scss';
import type { ITripsFiltersProps } from './types';

export const TripsFiltersDrawer: React.FC<ITripsFiltersProps> = (props) => {
  const { form, isOpen, onClose } = props;
  const { t } = useTranslation();
  const { filters, search, setFilters } = useTripFiltersStore();

  const soldToPartners = Form.useWatch('soldToPartners', form);

  const handleSuccess = debounce(() => transformAndSetFilters<ITripFilter>(form, setFilters), 300);

  const handleClearFilters = () => {
    setFilters(undefined);
    setTimeout(() => form.resetFields(), 0);
    onClose();
  };

  const clearButton = filters ? (
    <Button type='link' size='small' onClick={handleClearFilters}>
      {t('common:clear_filters')}
    </Button>
  ) : null;

  return (
    <Drawer placement='right' open={isOpen} extra={[clearButton]} onClose={onClose}>
      <Form form={form} name='trips-filter' layout='horizontal' initialValues={filters} className={styles.form}>
        <Space direction='vertical' size='middle' className={styles.box}>
          <Form.Item name='statuses' className={styles.input}>
            <Select placeholder='Statuses' mode='multiple' disabled={!!search} allowClear onChange={handleSuccess}>
              <Select.Option value='planning'>planning</Select.Option>
              <Select.Option value='ready'>ready</Select.Option>
              <Select.Option value='progress'>progress</Select.Option>
              <Select.Option value='done'>done</Select.Option>
            </Select>
          </Form.Item>
          <DataRangeFormPicker
            name='createdAt'
            handleSuccess={handleSuccess}
            isRequired={false}
            className={styles.input}
            rangePresets={filterRangePresets}
          />
          <WarehouseFormSelect
            name='loadingWarehouseIds'
            placeholder='Loading warehouse'
            className={styles.input}
            disabled={!!search}
            handleChange={handleSuccess}
            mode='multiple'
          />
          <WarehouseFormSelect
            name='unloadingWarehouseIds'
            placeholder='Unloading warehouse'
            className={styles.input}
            disabled={!!search}
            handleChange={handleSuccess}
            mode='multiple'
          />
          <UserFormSelect
            name='userId'
            placeholder={t('common:manager')}
            handleUserChange={handleSuccess}
            className={styles.input}
            disabled={!!search}
            allowClear
          />
          <Form.Item name='soldToPartners' className={styles.input} valuePropName='checked'>
            <Checkbox onChange={handleSuccess}>Sold to partners</Checkbox>
          </Form.Item>
          {soldToPartners && (
            <div>
              <ClientFormSelect
                label=''
                name='carrierId'
                placeholder='Select Carrier'
                handleClientChange={handleSuccess}
                clientTypes={[ClientType.carrier, ClientType.expedition]}
                allowClear
              />
            </div>
          )}
        </Space>
      </Form>
    </Drawer>
  );
};
